/**
 * ViewportModeContext — controls how an app's content area interacts with the
 * floating LiquidBubbleNav (FAB) at the bottom of the screen.
 *
 * Two modes:
 * - 'padded' (default): the scroll container that actually scrolls (Container
 *   leaf / Stack / Shell content) reserves --fab-clearance at its bottom as a
 *   page-coloured footer gap, so the FAB never covers the last item at max
 *   scroll. The gap carries the page background, so it reads as empty.
 * - 'full': no reservation; content uses the entire scrollable area and
 *   the FAB overlays content at all positions (use for fullscreen games
 *   or apps that already manage their own bottom inset).
 *
 * State is stored in a module-level singleton so every React tree on the
 * page (host app + bundled app) sees the same mode. The toggle item in the
 * FAB updates the singleton; all subscribers re-render. Persisted globally
 * (one shared preference for all apps) — toggling once "sticks" everywhere.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';

export type ViewportMode = 'padded' | 'full';

interface ViewportModeContextValue {
  mode: ViewportMode;
  setMode: (mode: ViewportMode) => void;
  toggleMode: () => void;
  /** Always true once any consumer has initialized the store. */
  available: boolean;
}

const STORAGE_KEY = 'room:viewport-mode';

// ---- Singleton store --------------------------------------------------------
// Module-level state, shared across every React tree on the page.

type Listener = () => void;

function readPersisted(): ViewportMode {
  if (typeof window === 'undefined') return 'padded';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'padded' || raw === 'full') return raw;
  } catch {
    // localStorage may throw (private mode, quota, etc.) — fall through
  }
  return 'padded';
}

function writePersisted(value: ViewportMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Ignore — persistence is best-effort
  }
}

let currentMode: ViewportMode = readPersisted();
const listeners = new Set<Listener>();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ViewportMode {
  return currentMode;
}

function getServerSnapshot(): ViewportMode {
  return 'padded';
}

function applyMode(next: ViewportMode) {
  if (currentMode === next) return;
  currentMode = next;
  writePersisted(next);
  listeners.forEach((l) => l());
}

// Keep the data-viewport-mode attribute on <html> in sync with the singleton
// (single source of truth for CSS rules anywhere in the page). Runs once at
// module load.
if (typeof document !== 'undefined') {
  const sync = () => {
    document.documentElement.dataset.viewportMode = currentMode;
  };
  sync();
  listeners.add(sync);
}

// ---- React API --------------------------------------------------------------

export interface ViewportModeProviderProps {
  children: ReactNode;
  /** Initial mode if nothing persisted. Defaults to 'padded'. */
  defaultMode?: ViewportMode;
  /** Retained for API compat; ignored — preference is now global. */
  appId?: string;
}

export function ViewportModeProvider({ children, defaultMode }: ViewportModeProviderProps) {
  // On first mount, if nothing was persisted, honor the requested default.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!window.localStorage.getItem(STORAGE_KEY) && defaultMode) {
        applyMode(defaultMode);
      }
    } catch {
      /* noop */
    }
  }, [defaultMode]);

  return <>{children}</>;
}

/** Read the current viewport mode and get helpers to change it. */
export function useViewportMode(): ViewportModeContextValue {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setMode = useCallback((next: ViewportMode) => applyMode(next), []);
  const toggleMode = useCallback(() => {
    applyMode(currentMode === 'padded' ? 'full' : 'padded');
  }, []);
  return useMemo(
    () => ({ mode, setMode, toggleMode, available: true }),
    [mode, setMode, toggleMode],
  );
}

/**
 * Pixel value of the FAB clearance area at the bottom of the viewport.
 * Returns 0 when mode is 'full'. Useful for game canvases that compute their
 * own dimensions in JS.
 */
export function useFabClearance(): number {
  const { mode } = useViewportMode();
  const subscribeResize = useCallback((listener: Listener) => {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);
  const compute = useCallback(() => {
    if (mode === 'full' || typeof document === 'undefined') return 0;
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--fab-clearance')
      .trim();
    const px = parseFloat(raw);
    return Number.isFinite(px) ? px : 0;
  }, [mode]);
  return useSyncExternalStore(subscribeResize, compute, () => 0);
}
